import { Button, Field, Input, Spinner, Textarea, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import type { Customer, CustomerWriteInput } from './contractsMirror.js';
import {
  useClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
} from './useClientsQuery.js';

/**
 * Create/edit client form — ONE component for `/clients/new` and
 * `/clients/$id/edit` (mode-switched, the EmployeeForm pattern). Fields: name,
 * email, phone, address, notes. The server owns the validation (D-03); the SPA
 * sends the raw fields and surfaces the server's error. `portal_enabled` is NOT
 * a form field — it belongs to the client page's Customer-portal switch.
 *
 * The primary CTA carries its SPECIFIC label ("Save client" / "Save changes",
 * A11Y-01). Every color is a locked token (BRAND-06).
 */

const SECTION = 'rounded-md border border-border bg-card p-6';

const editRouteApi = getRouteApi('/clients/$id/edit');

export interface ClientFormProps {
  mode: 'create' | 'edit';
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

function initialStateFrom(customer?: Customer): FormState {
  return {
    name: customer?.name ?? '',
    email: customer?.email ?? '',
    phone: customer?.phone ?? '',
    address: customer?.address ?? '',
    notes: customer?.notes ?? '',
  };
}

function ClientFormFields({
  mode,
  clientId,
  initial,
}: {
  mode: 'create' | 'edit';
  clientId?: number;
  initial: FormState;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = useCreateClientMutation();
  const updateMutation = useUpdateClientMutation(clientId ?? 0);
  const mutation = mode === 'create' ? createMutation : updateMutation;

  const [form, setForm] = useState<FormState>(initial);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submitKey = mode === 'create' ? 'clients:form.submitCreate' : 'clients:form.submitEdit';

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    // Trimmed, empty→null: an optional contact field is absent or a value, never ''.
    const payload: CustomerWriteInput = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      notes: form.notes.trim() || null,
    };
    mutation.mutate(payload, {
      onSuccess: (result) => {
        void navigate({ to: '/clients/$id', params: { id: String(result.customer.id) } });
      },
    });
  }

  return (
    <form className="flex max-w-3xl flex-col gap-4" onSubmit={onSubmit}>
      <section className={SECTION}>
        <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
          {t('clients:form.clientHeading')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('clients:form.name')} required className="sm:col-span-2">
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label={t('clients:form.email')}>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label={t('clients:form.phone')}>
            <Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label={t('clients:form.address')} className="sm:col-span-2">
            <Input value={form.address} onChange={(e) => set('address', e.target.value)} />
          </Field>
        </div>
        <Field label={t('clients:form.notes')} className="mt-4">
          <Textarea rows={4} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </section>

      {mutation.isError ? (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
        >
          {t('clients:form.saveError')}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? t('clients:form.saving') : t(submitKey)}
        </Button>
        <Button asChild variant="secondary">
          <Link to="/clients">{t('clients:form.cancel')}</Link>
        </Button>
      </div>
    </form>
  );
}

function EditClientForm() {
  const { t } = useTranslation();
  const { id } = editRouteApi.useParams();
  const clientId = Number(id);
  const query = useClientQuery(clientId);

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }
  if (query.isError || !query.data.customer) {
    return (
      <section role="alert" aria-live="assertive" className={SECTION}>
        <p className="font-medium text-foreground text-sm">{t('clients:error.detailHeading')}</p>
        <p className="mt-2 text-muted-foreground text-sm">{t('clients:error.detailBody')}</p>
      </section>
    );
  }

  return (
    <ClientFormFields
      mode="edit"
      clientId={clientId}
      initial={initialStateFrom(query.data.customer)}
    />
  );
}

export function ClientForm({ mode }: ClientFormProps) {
  const { t } = useTranslation();
  const headingKey = mode === 'create' ? 'clients:form.createHeading' : 'clients:form.editHeading';

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">{t(headingKey)}</h1>
      </header>
      {mode === 'create' ? (
        <ClientFormFields mode="create" initial={initialStateFrom()} />
      ) : (
        <EditClientForm />
      )}
    </div>
  );
}
